import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, X, Trash2, Quote, User, ThumbsUp, Loader2, MessageSquare, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const AdminReviews = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ average: 0, total: 0, breakdown: {} });

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchReviews();
    fetchStats();
  }, [admin, navigate]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get('/reviews/all');
      setReviews(response.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching reviews:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/reviews/stats');
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async (id, approved) => {
    try {
      await axios.put(`/reviews/${id}/approve`, { approved });
      fetchReviews();
      fetchStats();
    } catch (error) {
      logger.error('Error approving review:', error);
      alert('Failed to update review status.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await axios.delete(`/reviews/${id}`);
        fetchReviews();
        fetchStats();
      } catch (error) {
        logger.error('Error deleting review:', error);
        alert('Failed to delete review.');
      }
    }
  };

  const filterReviews = () => {
    if (filter === 'all') return reviews;
    if (filter === 'pending') return reviews.filter(r => !r.approved);
    if (filter === 'approved') return reviews.filter(r => r.approved);
    return reviews;
  };

  const filteredReviews = filterReviews();
  const pendingCount = reviews.filter(r => !r.approved).length;

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />

      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Reviews Management
            </h1>
            <p className="text-gray-400 mt-2">Monitor and moderate customer feedback</p>
          </div>
          {pendingCount > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-2 rounded-full flex items-center gap-2 animate-pulse">
              <AlertCircle className="w-4 h-4" />
              <span className="font-semibold">{pendingCount} pending reviews</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Average Rating', value: stats.average || 0, icon: Star, color: 'text-gold-500', border: 'border-gold-500/30' },
            { label: 'Total Reviews', value: stats.total || 0, icon: MessageSquare, color: 'text-blue-400', border: 'border-blue-500/30' },
            { label: 'Approved', value: reviews.filter(r => r.approved).length, icon: CheckCircle2, color: 'text-green-400', border: 'border-green-500/30' },
            { label: 'Pending', value: pendingCount, icon: Clock, color: 'text-amber-400', border: 'border-amber-500/30' }
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`bg-black/40 backdrop-blur-sm border ${stat.border}`}>
                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                  <div className={`w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className={`text-3xl font-bold mb-1 ${stat.color}`}>{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 bg-black/20 p-1.5 rounded-lg w-fit border border-white/10">
          {['all', 'pending', 'approved'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filter === f
                ? 'bg-gold-500 text-black shadow-lg shadow-gold-500/20'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="ml-2 text-xs opacity-60">
                ({f === 'all' ? reviews.length : f === 'pending' ? pendingCount : reviews.filter(r => r.approved).length})
              </span>
            </button>
          ))}
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-20 bg-black/20 rounded-xl border border-white/5">
            <MessageSquare className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">No {filter !== 'all' ? filter : ''} reviews found</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid gap-4"
          >
            <AnimatePresence>
              {filteredReviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`bg-gray-900/50 backdrop-blur-sm border transition-all hover:bg-gray-900/80 ${review.approved ? 'border-green-500/20 hover:border-green-500/40' : 'border-amber-500/20 hover:border-amber-500/40'
                    }`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* User Info */}
                        <div className="flex md:flex-col items-center md:items-start gap-4 md:w-48 shrink-0">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-black shadow-lg shadow-gold-500/10">
                            <User className="w-6 h-6" />
                          </div>
                          <div className="md:text-left">
                            <h3 className="text-gold-200 font-bold truncate max-w-[150px]">{review.name}</h3>
                            {review.email && (
                              <p className="text-gray-500 text-xs truncate max-w-[180px]">{review.email}</p>
                            )}
                            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400 bg-white/5 px-2 py-1 rounded w-fit">
                              <Clock className="w-3 h-3" />
                              {new Date(review.createdAt).toLocaleDateString('en-PK')}
                            </div>
                          </div>
                        </div>

                        {/* Review Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
                            <div className="flex items-center gap-2">
                              <div className="flex bg-black/40 px-2 py-1 rounded border border-white/10">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating ? 'text-gold-400 fill-gold-400' : 'text-gray-700'}`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-bold text-white ml-1">{review.rating}.0</span>
                            </div>

                            <div className="flex items-center gap-2">
                              {review.orderNumber && (
                                <span className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full text-xs border border-blue-500/20">
                                  Order #{review.orderNumber}
                                </span>
                              )}
                              <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${review.approved
                                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                }`}>
                                {review.approved ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                {review.approved ? 'Approved' : 'Pending'}
                              </span>
                            </div>
                          </div>

                          <div className="relative bg-black/20 rounded-lg p-4 border border-white/5 mb-4">
                            <Quote className="text-gold-500/20 absolute top-2 left-2 w-8 h-8" />
                            <p className="text-gray-300 relative z-10 pl-2 leading-relaxed italic">
                              "{review.comment}"
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="text-gray-400 text-sm flex items-center gap-1 bg-white/5 px-3 py-1.5 rounded-full">
                              <ThumbsUp className="w-4 h-4 text-gold-400" />
                              <span className="text-white font-medium">{review.helpful || 0}</span> people found this helpful
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              {!review.approved ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(review.id, true)}
                                  className="bg-green-600 hover:bg-green-500 text-white border-none shadow-none"
                                >
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApprove(review.id, false)}
                                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                                >
                                  <X className="w-4 h-4 mr-1" /> Unapprove
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(review.id)}
                                className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;


